import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { IMD_DEFAULTS, VERY_HEAVY_SEVERITY, validateThresholds } from './heavyRain.service';

const empty = { veryHeavyMm: '', extremelyHeavyMm: '', extremelyHeavySeverity: '', source: '', notes: '' };

export default function HeavyRainModal({ open, countryName, thresholds, onClose, onSave }) {
    const [draft, setDraft] = useState(empty);
    const [issues, setIssues] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDraft(thresholds ? { ...thresholds } : empty);
        setIssues({});
        setSaving(false);
    }, [open, thresholds]);

    const set = (field) => (event) => {
        setDraft((prev) => ({ ...prev, [field]: event.target.value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = async () => {
        const found = validateThresholds(draft);
        setIssues(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const saved = await onSave(draft);
        if (!saved) setSaving(false);
    };

    const numberField = (field, label, helper, adornment) => (
        <Box>
            <FieldLabel label={label} required />
            <TextField
                fullWidth
                type="number"
                value={draft[field]}
                onChange={set(field)}
                error={Boolean(issues[field])}
                helperText={issues[field] || helper}
                InputProps={adornment ? { endAdornment: <Typography sx={{ color: '#625B71', ml: 1 }}>{adornment}</Typography> } : undefined}
                sx={formFieldSx}
            />
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {thresholds ? 'Edit' : 'Set'} heavy-rain thresholds{countryName ? ` for ${countryName}` : ''}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    {numberField('veryHeavyMm', 'Very heavy from', `Consecutive days at or above this are one event (severity ${VERY_HEAVY_SEVERITY}).`, 'mm')}
                    {numberField('extremelyHeavyMm', 'Extremely heavy from', 'One day at or above this gives the whole spell the higher severity.', 'mm')}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    {numberField('extremelyHeavySeverity', 'Extremely heavy severity', 'On the distress scale, 0 to 10. Heavy rain alone is capped at 5.')}
                    <Box>
                        <FieldLabel label="Source" required />
                        <TextField
                            fullWidth
                            value={draft.source}
                            onChange={set('source')}
                            placeholder="IMD"
                            error={Boolean(issues.source)}
                            helperText={issues.source || 'The weather agency; shown on every event.'}
                            sx={formFieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FieldLabel label="Notes" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={draft.notes}
                        onChange={set('notes')}
                        placeholder="Where these values come from"
                        sx={formFieldSx}
                    />
                </Box>

                {!thresholds && (
                    <Button
                        onClick={() => {
                            setDraft({ ...IMD_DEFAULTS });
                            setIssues({});
                        }}
                        sx={{ textTransform: 'none', color: '#294190', px: 0 }}
                    >
                        Fill in IMD's categories (India)
                    </Button>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Saving…' : 'Save thresholds'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
